import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Grid, InputAdornment, MenuItem, Paper, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import useNotify from '../../hooks/useNotify';
import PageWithTitle from '../../components/atom/PageWithTitle';
import { TABLE_ROW_COUNT_DEFAULT, PAID_STATUSES } from '../../helpers/constants';
import { PARKINGS_TABLE_COLUMNS } from '../../helpers/tableColumnLists';
import GenericTable from '../../components/atom/GenericTable';
import handleNavigation from '../../helpers/handleNavigation';
import ExportParkingsButton from './components/ExportParkingsButton';
import { PARKING_STATES, parkingTicketRow } from './parkingFormat';
import { looksLikePlate, normalizePlate } from '../liveparking/plateMatch';

const PAID_PARAMS = {
    [PAID_STATUSES.ALL]: '',
    [PAID_STATUSES.PAID]: 1,
    [PAID_STATUSES.NOT_PAID]: 0,
};

// text input that only reports its value after the user stopped typing
function useDebounced(value, ms = 500) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), ms);
        return () => clearTimeout(t);
    }, [value, ms]);
    return debounced;
}

const ParkingsPage = () => {
    const { notifyError } = useNotify();
    const location = useLocation();
    const navigate = useNavigate();

    const [search, setSearch] = useState('');
    const [userId, setUserId] = useState('');
    const [state, setState] = useState('ALL');
    const [paidStatus, setPaidStatus] = useState(PAID_STATUSES.ALL);
    const searchStr = useDebounced(search.trim());
    const userIdStr = useDebounced(userId.trim());

    const [{ limit, page, sortorder, sortby }, setParams] = useState({
        limit: TABLE_ROW_COUNT_DEFAULT,
        page: 0,
        sortorder: 'DESC',
        sortby: 'entryTime',
    });

    // back to the first page whenever a filter changes
    useEffect(() => {
        setParams((p) => ({ ...p, page: 0 }));
    }, [searchStr, userIdStr, state, paidStatus]);

    const [data, loading, error] = useApi('ParkingTickets', 'getParkingTickets', {
        limit,
        offset: page * limit,
        search: searchStr || undefined,
        searchUserId: userIdStr || undefined,
        searchPayed: PAID_PARAMS[paidStatus] === '' ? undefined : PAID_PARAMS[paidStatus],
        ...PARKING_STATES[state].params,
        sortorder,
        sortby,
    });

    useEffect(() => {
        if (error) {
            notifyError(error);
        }
    }, [error, notifyError]);

    const openDetails = (id) => {
        handleNavigation(location, (stateObj) => {
            navigate(`/parkings/details/${encodeURIComponent(id)}`, stateObj);
        });
    };

    const rows = useMemo(() => (data?.tickets || []).map(parkingTicketRow), [data?.tickets]);
    // a plate that did not park with the app has no ticket here, its page is built from the parking system's events
    const plate = normalizePlate(searchStr);
    const offerEvents = !loading && data?.total === 0 && looksLikePlate(plate);

    return (
        <PageWithTitle title="Parkolások" headline={<ExportParkingsButton total={data?.total} />}>
            <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Rendszám / jegyszám"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Állapot"
                                value={state}
                                onChange={(e) => setState(e.target.value)}
                            >
                                {Object.keys(PARKING_STATES).map((key) => (
                                    <MenuItem key={key} value={key}>
                                        {PARKING_STATES[key].label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Fizetés"
                                value={paidStatus}
                                onChange={(e) => setPaidStatus(e.target.value)}
                            >
                                {Object.values(PAID_STATUSES).map((label) => (
                                    <MenuItem key={label} value={label}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Felhasználó ID"
                                value={userId}
                                onChange={(e) => setUserId(e.target.value.replace(/\D/g, ''))}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper variant="outlined">
                    <GenericTable
                        rows={rows}
                        limit={limit}
                        page={page}
                        setParams={setParams}
                        loading={loading}
                        total={data?.total}
                        order={sortorder}
                        orderBy={sortby}
                        columns={PARKINGS_TABLE_COLUMNS}
                        cellPadding="normal"
                        size="small"
                        onRowClick={(row) => openDetails(row.id)}
                    />
                </Paper>
            </Grid>
            {offerEvents && (
                <Grid item xs={12}>
                    <Alert
                        severity="info"
                        action={
                            <Button color="inherit" size="small" onClick={() => openDetails(plate)}>
                                Megnyitás
                            </Button>
                        }
                    >
                        Nincs app alapú parkolás ezzel a rendszámmal. A {plate} parkolását a parkoló
                        rendszer eseményeiből is meg lehet nézni.
                    </Alert>
                </Grid>
            )}
        </PageWithTitle>
    );
};

export default ParkingsPage;
